import { useQuery } from '@tanstack/react-query';
import {
  EquipmentSearchResponse,
  EquipmentSearchSuggestionsResponse,
  EquipmentSuggestionsQueryParams,
  EquipmentSuggestionsResponse
} from './types';
import { getRelativeURLWithParams } from 'utils/url';
import { getRequestId } from 'utils/requestId';
import { REQUEST_ID_HEADER } from 'constants/headers';
import {
  EQUIPMENT_SEARCH,
  EQUIPMENT_SEARCH_SUGGESTIONS
} from 'constants/endpoints.api';
import {
  EQUIPMENT_SEARCH_FILTER,
  INTERNAL_SERVER_ERROR
} from 'components/my-equipment/constants';

export const useEquipmentSearchSuggestionsQuery = ({
  catalogId,
  filter,
  langId,
  searchTerm,
  storeId,
  isValid,
  isEditEquipment,
  locale,
  esiteIdentifier
}: EquipmentSuggestionsQueryParams & {
  isEditEquipment?: boolean;
  locale?: string;
  esiteIdentifier?: string;
}) =>
  useQuery<EquipmentSearchSuggestionsResponse>({
    queryKey: [
      'equipmentSuggestions',
      searchTerm,
      filter,
      langId,
      storeId,
      catalogId
    ],
    queryFn: async () => {
      const equipmentSearchUrl = isEditEquipment
        ? EQUIPMENT_SEARCH
        : EQUIPMENT_SEARCH_SUGGESTIONS;
      const url = getRelativeURLWithParams(equipmentSearchUrl, {
        q: searchTerm.toLowerCase(),
        filter,
        langId,
        storeId,
        catalogId,
        locale,
        esiteIdentifier
      });

      const res = await fetch(url, {
        headers: { [REQUEST_ID_HEADER]: getRequestId() }
      });
      if (!res.ok) {
        if (res.status === 400) {
          const isFilteredBySerial = filter === EQUIPMENT_SEARCH_FILTER.SERIAL;
          const errorMessage = isFilteredBySerial
            ? 'MEQ_SERIAL_NO_MATCH'
            : 'MEQ_MODEL_NO_MATCH';
          throw new Error(errorMessage);
        } else if (res.status === INTERNAL_SERVER_ERROR) {
          return { status: INTERNAL_SERVER_ERROR };
        }
      }
      if (isEditEquipment) {
        return res.json() as Promise<EquipmentSearchResponse>;
      }
      return res.json() as Promise<EquipmentSuggestionsResponse>;
    },
    // Query config
    enabled: isValid
  });
